import { AuditableEntity, ID } from '../types/common';
import { DomainError, ValidationError } from '../errors/domainError';
import { ErrorCode } from '../errors/errorCodes';

export type FolderType = 'regular' | 'special';

export interface FolderProps {
  id: ID;
  name: string;
  color: string;
  icon?: string;
  type: FolderType;
  isSpecial?: boolean;
  parentId?: ID;
  order: number;
  createdAt: number;
  updatedAt: number;
  createdBy?: ID;
  updatedBy?: ID;
}

export class Folder implements AuditableEntity {
  public readonly id: ID;
  public readonly name: string;
  public readonly color: string;
  public readonly icon?: string;
  public readonly type: FolderType;
  public readonly parentId?: ID;
  public readonly order: number;
  public readonly createdAt: number;
  public readonly updatedAt: number;
  public readonly createdBy?: ID;
  public readonly updatedBy?: ID;

  private static readonly RESERVED_NAMES = [
    'All Prompts',
    'Favorites',
    'Recently Used',
    'Archived',
  ];

  private static readonly COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

  protected constructor(props: FolderProps) {
    this.id = props.id;
    this.name = props.name;
    this.color = props.color;
    this.icon = props.icon;
    this.type = props.type;
    this.parentId = props.parentId;
    this.order = props.order;
    this.createdAt = props.createdAt;
    this.updatedAt = props.updatedAt;
    this.createdBy = props.createdBy;
    this.updatedBy = props.updatedBy;

    Object.freeze(this);
  }

  public static create(
    props: Omit<FolderProps, 'id' | 'createdAt' | 'updatedAt' | 'type'>
  ): Folder {
    this.validateName(props.name);
    this.validateColor(props.color);

    const now = Date.now();
    return new Folder({
      ...props,
      id: crypto.randomUUID(),
      type: 'regular',
      isSpecial: false,
      createdAt: now,
      updatedAt: now,
      order: props.order ?? 0,
    });
  }

  public static createSpecial(name: string, color: string): Folder {
    if (!this.RESERVED_NAMES.includes(name)) {
      throw new ValidationError('Invalid special folder name');
    }

    const now = Date.now();
    return new Folder({
      id: crypto.randomUUID(),
      name,
      color,
      type: 'special',
      isSpecial: true,
      order: 0,
      createdAt: now,
      updatedAt: now,
    });
  }

  public static fromProps(props: FolderProps): Folder {
    return new Folder({
      ...props,
      order: props.order ?? 0,
    });
  }

  private static validateName(name: string): void {
    if (!name.trim()) {
      throw new ValidationError('Folder name cannot be empty');
    }
    if (name.length > 50) {
      throw new ValidationError('Folder name cannot be longer than 50 characters');
    }
    if (this.RESERVED_NAMES.includes(name)) {
      throw new ValidationError('Folder name is reserved for special folders');
    }
  }

  private static validateColor(color: string): void {
    if (!this.COLOR_REGEX.test(color)) {
      throw new ValidationError('Invalid color format. Must be a valid hex color (e.g., #FF0000)');
    }
  }

  public update(
    changes: Partial<Omit<FolderProps, 'id' | 'type' | 'createdAt' | 'updatedAt'>>
  ): Folder {
    if (this.type === 'special') {
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Special folders cannot be modified'
      );
    }

    const updatedProps = {
      ...this.toObject(),
      ...changes,
      updatedAt: Date.now(),
    };

    // Re-validate the folder
    if (changes.name) {
      Folder.validateName(changes.name);
    }
    if (changes.color) {
      Folder.validateColor(changes.color);
    }

    return new Folder(updatedProps);
  }

  public updateOrder(newOrder: number): Folder {
    if (this.type === 'special') {
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Special folders order cannot be modified'
      );
    }

    return this.update({ order: newOrder });
  }

  public moveToParent(parentId: ID | undefined): Folder {
    if (this.type === 'special') {
      throw new DomainError(
        ErrorCode.SPECIAL_FOLDER_MODIFICATION,
        'Special folders cannot be moved'
      );
    }

    if (parentId === this.id) {
      throw new ValidationError('Folder cannot be its own parent');
    }

    return this.update({ parentId });
  }

  public isSpecial(): boolean {
    return this.type === 'special';
  }

  public toObject(): FolderProps {
    return {
      id: this.id,
      name: this.name,
      color: this.color,
      icon: this.icon,
      type: this.type,
      isSpecial: this.type === 'special',
      parentId: this.parentId,
      order: this.order,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      createdBy: this.createdBy,
      updatedBy: this.updatedBy,
    };
  }

  public equals(other: Folder): boolean {
    return this.id === other.id;
  }
}
